import { useState } from 'react';
import { Container, Row, Col, Card, Modal, Button } from 'react-bootstrap';
import { 
  Brain, 
  LineChart, 
  Code2, 
  Layers, 
  Cloud, 
  GitBranch,
  Monitor,
  CheckCircle,
  Users,
  Award,
  BookOpen,
  Briefcase
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { PageBanner } from '../components/layout/PageBanner';
import { TrainingCard } from '../components/common/TrainingCard';

const Training = () => {
  const { t, language } = useLanguage();
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);

  const handleKnowMore = (title) => {
    setSelectedWorkshop(title);
  };

  const handleClose = () => {
    setSelectedWorkshop(null);
  };

  // Workshop Programs
  const trainingPrograms = [
    {
      icon: Monitor,
      title: t('training.robotics'),
      description: t('training.roboticsDesc'),
      duration: 3
    },
    {
      icon: Brain,
      title: t('training.aiTraining'),
      description: t('training.aiTrainingDesc'),
      duration: 2
    },
    {
      icon: LineChart,
      title: t('training.ml'),
      description: t('training.mlDesc'),
      duration: 2
    },
    {
      icon: Code2,
      title: t('training.python'),
      description: t('training.pythonDesc'),
      duration: 2
    },
    {
      icon: Layers,
      title: t('training.fullstack'),
      description: t('training.fullstackDesc'),
      duration: 3
    },
    {
      icon: Cloud,
      title: t('training.cloudTraining'),
      description: t('training.cloudTrainingDesc'),
      duration: 2
    },
    {
      icon: GitBranch,
      title: t('training.devops'),
      description: t('training.devopsDesc'),
      duration: 2
    }
  ];

  const highlightIcons = { BookOpen, Users, Award, Briefcase };
  const highlights = [
    { iconName: 'BookOpen', text: t('training.highlight1') },
    { iconName: 'Users', text: t('training.highlight2') },
    { iconName: 'Award', text: t('training.highlight3') },
    { iconName: 'Briefcase', text: t('training.highlight4') }
  ];

  return (
    <main id="main-content">
      <PageBanner 
        title={t('training.pageTitle')} 
        breadcrumb={t('training.breadcrumb')} 
      />

      {/* Introduction */}
      <section className="gov-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <p style={{ 
                fontSize: '1.125rem', 
                color: 'hsl(var(--muted-foreground))',
                textAlign: 'center',
                lineHeight: 1.7,
                marginBottom: '3rem'
              }}>
                {t('training.intro')}
              </p>

              {/* Highlights */}
              <div 
                style={{
                  background: 'var(--gradient-gov-banner)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '2rem',
                  color: 'white'
                }}
              >
                <h3 style={{ 
                  fontSize: '1.25rem', 
                  fontWeight: '600', 
                  marginBottom: '1.5rem',
                  textAlign: 'center'
                }}>
                  {t('training.highlights')}
                </h3>
                <Row className="g-3">
                  {highlights.map((item, index) => {
                    const IconComponent = highlightIcons[item.iconName];
                    return (
                      <Col key={index} sm={6} lg={3}>
                        <div 
                          className="d-flex align-items-center gap-3 p-3"
                          style={{
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: 'var(--radius)'
                          }}
                        >
                          <IconComponent size={24} />
                          <span style={{ fontSize: '0.9375rem' }}>{item.text}</span>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Workshop Grid */}
      <section className="gov-section gov-section-alt" style={{ paddingTop: 0 }}>
        <Container>
          <div className="text-center mb-5">
            <h2 className="gov-section-heading">
              {language === 'hi' ? 'उपलब्ध कार्यशालाएं' : 'Available Workshops'}
            </h2>
          </div>

          <Row className="g-4">
            {trainingPrograms.map((program, index) => (
              <Col key={index} md={6} lg={4}>
                <TrainingCard 
                  {...program}
                  onKnowMore={handleKnowMore}
                />
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Modal */}
      <Modal show={!!selectedWorkshop} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedWorkshop}</Modal.Title>
        </Modal.Header>

        <Modal.Body>

  {/* ================= ROBOTICS ================= */}
{selectedWorkshop === t('training.robotics') && (
  <>
    <h5>Workshop Duration</h5>
    <p>
      2–3 Days Intensive Hands-on Training Program
      <br />
      (4–5 Hours per Day)
    </p>

    <h5>Program Overview</h5>
    <p>
      The Robotics & Innovation Workshop is a practical, project-based
      learning program designed to introduce students to modern technologies
      including Robotics, Automation, Electronics, Programming, IoT,
      and Intelligent Systems through real-world applications and
      interactive experimentation.
    </p>

    <h5>Technology Kits & Components</h5>
    <ul>
      <li>
        <strong>Foundation Level – Robotics Starter System:</strong><br />
        Arduino Uno/Nano board, robotic chassis, DC motors with wheels,
        L298N motor driver, IR sensor modules, battery system,
        jumper wires, breadboard, and essential electronic components
        for circuit development and robotics prototyping.
      </li>

      <li>
        <strong>Automation Level – Smart Robotics System:</strong><br />
        Includes all Foundation Level components along with Ultrasonic
        sensors, Servo motors, Bluetooth communication module (HC-05/HC-06),
        upgraded power management system, and enhanced obstacle
        detection capabilities for intelligent robotic behavior.
      </li>

      <li>
        <strong>Innovation Level – Raspberry Pi AI & IoT System:</strong><br />
        Raspberry Pi 3 Model B/B+, pre-configured 32GB Micro SD card,
        Pi Camera Module, WiFi-enabled connectivity, advanced motor
        driver system, programmable Python-based automation,
        smart surveillance integration, and IoT-enabled control modules.
      </li>
    </ul>

    <h5>Projects Developed During Workshop</h5>
    <ul>
      <li>Autonomous Line Follower Robot</li>
      <li>Obstacle Avoidance Smart Robot</li>
      <li>Bluetooth Controlled Wireless Robot</li>
      <li>Raspberry Pi Based Smart Surveillance Robot</li>
      <li>Basic IoT-Based Automation Demonstration</li>
    </ul>

    <h5>Topics Covered</h5>
    <ul>
      <li>Introduction to Robotics, Automation & Smart Systems</li>
      <li>Electronics Fundamentals (Voltage, Current, Circuits)</li>
      <li>Sensor Technologies & Real-Time Data Processing</li>
      <li>Motor Control Systems & Driver Modules</li>
      <li>Arduino Programming & Embedded Systems Basics</li>
      <li>Introduction to Raspberry Pi & Linux Environment</li>
      <li>Python Programming for Robotics Applications</li>
      <li>IoT, Automation & Smart Device Concepts</li>
      <li>Innovation-Based Team Challenge & Problem Solving</li>
    </ul>

    <h5>Learning Outcomes</h5>
    <ul>
      <li>Hands-on robotics assembly and system integration experience</li>
      <li>Understanding of embedded systems and automation technologies</li>
      <li>Development of logical thinking and computational skills</li>
      <li>Introduction to AI, IoT, and smart technology ecosystems</li>
      <li>Team collaboration, innovation, and project execution skills</li>
      <li>Exposure to real-world engineering and automation concepts</li>
    </ul>

    <h5>Workshop Benefits</h5>
    <ul>
      <li>Project-based experiential learning approach</li>
      <li>Industry-relevant technology exposure</li>
      <li>Encourages creativity, innovation, and problem solving</li>
      <li>Designed to support future-ready technical skills</li>
      <li>Suitable for school innovation and STEM learning initiatives</li>
    </ul>

    <h5>Certification</h5>
    <p>
      All participants will receive an official Workshop Participation
      Certificate from Imagenators Global Technology Pvt. Ltd.
    </p>
  </>
)}

  {/* ================= AI WORKSHOP ================= */}
  {selectedWorkshop === t('training.aiTraining') && (
    <>
      <h5>Workshop Duration</h5>
      <p>2 Days (4–5 hours per day)</p>

      <h5>Topics Covered</h5>
      <ul>
        <li>What is Artificial Intelligence?</li>
        <li>Real-world AI Applications</li>
        <li>Machine Learning Basics</li>
        <li>AI in Education & Industry</li>
        <li>Live Demonstrations of AI Tools</li>
        <li>Interactive AI Activity Session</li>
      </ul>

      <h5>Learning Outcomes</h5>
      <ul>
        <li>Understanding AI fundamentals</li>
        <li>Awareness of emerging technologies</li>
        <li>Practical exposure to AI tools</li>
      </ul>
    </>
  )}

  {/* ================= DATA SCIENCE ================= */}
  {selectedWorkshop === t('training.ml') && (
    <>
      <h5>Workshop Duration</h5>
      <p>2 Days (4–5 hours per day)</p>

      <h5>Topics Covered</h5>
      <ul>
        <li>Introduction to Data & Analytics</li>
        <li>Data Visualization Basics</li>
        <li>Understanding Patterns & Trends</li>
        <li>Introduction to Machine Learning Concepts</li>
        <li>Mini Data Analysis Activity</li>
      </ul>
    </>
  )}

  {/* ================= PYTHON ================= */}
  {selectedWorkshop === t('training.python') && (
    <>
      <h5>Workshop Duration</h5>
      <p>2 Days (4–5 hours per day)</p>

      <h5>Topics Covered</h5>
      <ul>
        <li>Introduction to Programming</li>
        <li>Variables, Conditions & Loops</li>
        <li>Basic Logic Building</li>
        <li>Simple Application Development</li>
        <li>Mini Coding Project</li>
      </ul>
    </>
  )}

  {/* ================= WEB DEVELOPMENT ================= */}
  {selectedWorkshop === t('training.fullstack') && (
    <>
      <h5>Workshop Duration</h5>
      <p>3 Days (4–5 hours per day)</p>

      <h5>Topics Covered</h5>
      <ul>
        <li>Introduction to Web Development</li>
        <li>HTML & CSS Basics</li>
        <li>Responsive Design Concepts</li>
        <li>Website Deployment Basics</li>
        <li>Build & Deploy a Mini Website</li>
      </ul>
    </>
  )}

  {/* ================= CLOUD ================= */}
  {selectedWorkshop === t('training.cloudTraining') && (
    <>
      <h5>Workshop Duration</h5>
      <p>2 Days (4–5 hours per day)</p>

      <h5>Topics Covered</h5>
      <ul>
        <li>What is Cloud Computing?</li>
        <li>Introduction to AWS/Azure</li>
        <li>Cloud Infrastructure Basics</li>
        <li>Deployment Concepts</li>
        <li>Live Cloud Demonstration</li>
      </ul>
    </>
  )}

  {/* ================= DEVOPS ================= */}
  {selectedWorkshop === t('training.devops') && (
    <>
      <h5>Workshop Duration</h5>
      <p>2 Days (4–5 hours per day)</p>

      <h5>Topics Covered</h5>
      <ul>
        <li>Introduction to DevOps</li>
        <li>Version Control (Git Basics)</li>
        <li>CI/CD Concept Overview</li>
        <li>Automation Fundamentals</li>
        <li>Live Demonstration</li>
      </ul>
    </>
  )}

</Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            {language === 'hi' ? 'बंद करें' : 'Close'}
          </Button>
        </Modal.Footer>
      </Modal>

    </main>
  );
};

export default Training;